import { flatten, size, values, cloneDeep, map, keysIn, pick, indexOf, chunk } from 'lodash'

export class FlexTable {
  constructor (schema, data, orientation) {
    this.SETTINGS = {
      HORIZONTAL: 0,
      VERTICAL: 1
    }
    this.fixedData = []
    this.schema = cloneDeep(schema)
    this.data = cloneDeep(data)
    this.orientation = this.setTableOrientation(orientation)
    this.deleted = []
  }

  setTableOrientation (setting) {
    if (this.SETTINGS.HORIZONTAL === setting) {
      return this.SETTINGS.HORIZONTAL
    } else if (this.SETTINGS.VERTICAL === setting) {
      return this.SETTINGS.VERTICAL
    } else {
      throw Error('Orientation can only receive 0 or 1.')
    }
  }

  getRowName (obj) {
    return Object.keys(obj).map(e => obj[e].name)
  }

  formatAsMoney (input) {
    if (typeof input === 'string') {
      if (input.indexOf('-') > -1) {
        let [upperRange, lowerRange] = input.split('-')
        return `${this.formatAsMoney(upperRange)} - ${this.formatAsMoney(lowerRange)}`
      }
      return `$ ${this.commaTose(input, 3)}`
    } else if (parseInt(input) === input) {
      return `$ ${this.commaTose(input, 3)}.00`
    } else if (typeof input === 'number') {
      return `$ ${this.commaTose(input, 3)}`
    }
  }

  commaTose (value, interval) {
    let [input, decimal] = value.toString().split('.')
    // Extract the value of integer and decimal part separately
    // eg: 54226556565
    input = input.split('').reverse()
    // reversal to get ["5","6","5","6","5","5","6","2","2","4","5"]
    let chunks = chunk(input, interval)
    // chunks: [["5","6","5"],["6","5","5"],["6","2","2"],["4","5"]]
    chunks = map(chunks, e => e.reverse().join(''))
    // chunks: ["565","556","226","54"]
    chunks = chunks.reverse().join(',')
    // 54,226,556,565
    if (decimal) {
      chunks = `${chunks}.${decimal}`
    }
    // return after adding decimals if any
    return chunks
  }

  makeEditable (input) {
    return {
      value: input,
      isEditable: true
    }
  }

  formatAsPercentage (input) {
    return `${input} %`
  }

  delete (val, obj, key) {
    this.deleted.push({val: obj[key], key: key})
    delete obj[key]
  }

  getFixedFromSchema () {
    let rows = []
    let propertiesInSchema = Object.keys(this.schema)
    if (this.orientation === this.SETTINGS.VERTICAL) {
      rows = propertiesInSchema.map(e => {
        let childProperties = Object.keys(this.schema[e].contains)
        if (e === 'basic_facts' && childProperties[0] === 'itemId') {
          childProperties.shift()
        }
        let partial = []
        for (let i = 0; i < childProperties.length; i++) {
          if (i === 0) {
            partial.push({ name: this.schema[e].contains[childProperties[i]].name, parent: this.schema[e].name, parent_span: childProperties.length })
          } else {
            partial.push({ name: this.schema[e].contains[childProperties[i]].name })
          }
        }
        return partial
      })
    }
    return flatten(rows)
  }

  getDataFromSchema (screenName, status) {
    let data = cloneDeep(this.data)
    let functionable = this.data.my_offer && !this.data.lock
    let propStore = {
      lock: data.lock,
      my_offer: data.my_offer
    }
    delete data.lock
    delete data.my_offer
    let dataFields = Object.keys(data)
    let formattedData = []
    dataFields.forEach(field => {
      let datacols = data[field].map((el, i, ctx) => {
        let propInField = Object.keys(el)
        propInField.forEach(prop => {
          if (prop === 'pet_depot_funding' && screenName === 'offer_select' && functionable) {
            el[prop] = this['makeEditable'](el[prop])
          } else {
            try {
              this.schema[field].contains[prop].fn.forEach(fn => {
                if (fn !== 'delete') {
                  el[prop] = this[fn](el[prop], el, prop)
                } else {
                  this[fn](el[prop], el, prop)
                }
              })
            } catch (e) {
              console.log(e)
            }
          }
        })
        return el
      })

      formattedData.push(datacols)
    })
    for (let prop in propStore) {
      if (!isNaN(Number(prop))) throw Error('Got property as a number')
      formattedData[prop] = propStore[prop]
    }
    console.log(formattedData)
    return formattedData
  }

  dataTableStyle (screenName, status) {
    let data = cloneDeep(this.data)
    let dataKeys = Object.keys(data)
    let tableData = []
    let partials
    let formattedData
    let propStore
    if (status) {
      formattedData = this.getDataFromSchema(screenName, status)
    } else {
      formattedData = this.getDataFromSchema(screenName)
    }
    propStore = {
      lock: formattedData.lock,
      my_offer: formattedData.my_offer
    }
    console.log(formattedData)
    let dataSize = dataKeys.reduce((acc, key) => {
      return (size(data[key]) > acc) ? size(data[key]) : acc
    }, 0)

    for (let i = 0; i < dataSize; i++) {
      partials = []
      formattedData.forEach(data => {
        partials.push(values(data[i]))
      })
      tableData.push(flatten(partials))
      tableData.forEach((e, i) => {
        if (this.deleted[i]) {
          e[this.deleted[i].key] = this.deleted[i].val
        }
      })
    }
    for (let prop in propStore) {
      if (!isNaN(Number(prop))) throw Error('Got property as a number')
      tableData[prop] = propStore[prop]
    }
    return tableData
  }

  getTableData () {
    let data = cloneDeep(this.data)
    let dataKeys = Object.keys(data)
    let tableData = []

    let dataSize = dataKeys.reduce((acc, key) => {
      return (size(data[key]) > acc) ? size(data[key]) : acc
    }, 0)

    for (let i = 0; i < dataSize; i++) {
      let dataCollector = []
      dataKeys.forEach(key => {
        Object.keys(data[dataKeys][i])
        dataCollector.push(data[dataKeys][i])
      })
      tableData.push(dataCollector)
    }
    return tableData
  }

  getHorizontalTableHeader (inputHeaderJsonSchema, key) {
    return map(inputHeaderJsonSchema.header, key)
  }

  getHorizontalTableData (inputHeaderJsonSchema, inputDataJsonSchema, fnKey) {
    let finalJsonArray = []
    const input = inputDataJsonSchema.data
    for (let i = 0; i < input.length; i++) {
      let eachJsonArray = []
      let data = input[i]
      for (let key in data) {
        let resultantData = {}
        resultantData.value = data[key]
        resultantData.edit = false
        resultantData.editable = inputHeaderJsonSchema.header[key].fn.includes(fnKey)
        eachJsonArray.push(resultantData)
      }
      finalJsonArray.push(eachJsonArray)
    }
    return finalJsonArray
  }

  getObjectTableHeaders (headerSchema, ...keys) {
    let listHeaders = []
    for (let offer in headerSchema) {
      if (keys !== undefined && keys.indexOf(headerSchema[offer].name) === -1) {
        listHeaders.push({value: headerSchema[offer].name, key: offer})
      }
    }
    return listHeaders
  }

  getStringTableHeaders (headerSchema) {
    return Object.keys(headerSchema)
  }

  getHeadersForFixedTable (headerSchema, screenName) {
    let headersSchemaKeys = keysIn(headerSchema)
    let fixedRowKeys = headersSchemaKeys.filter(e => headerSchema[e].fixed)
    return fixedRowKeys.map((e) => {
      const obj = {}
      obj.value = headerSchema[e].name
      return obj
    })
  }

  getHeadersForScrollableTable (headerSchema, fixedRowKeys) {
    let scrollableRowKeys = keysIn(headerSchema).filter(e => fixedRowKeys.indexOf(e) === -1)
    return scrollableRowKeys.map((e) => {
      const obj = {}
      obj.value = headerSchema[e].name
      return obj
    })
  }

  getMapHeaders (headerSchema, fixedRowKeys) {
    return keysIn(headerSchema).filter(e => fixedRowKeys.indexOf(e) === -1)
  }

  getLinkableHeaders (headerSchema, key) {
    let headersSchemaKeys = keysIn(headerSchema)
    let fixedRowKeys = headersSchemaKeys.filter(e => headerSchema[e] && headerSchema[e].fn && headerSchema[e].fn.includes(key))
    return fixedRowKeys
  }

  getIndexforFillteredTableHeaders (headerSchema, key) {
    let headersSchemaKeys = keysIn(headerSchema)
    let fixedRowKeys = headersSchemaKeys.filter(e => headerSchema[e].fn.includes(key))
    let keyIndexes = fixedRowKeys.map(e => {
      return indexOf(headersSchemaKeys, e)
    })
    return keyIndexes
  }

  getTableDataAsObjectArray (keys, data, cellStyle, hasLinkIndex) {
    var cols = keysIn(data)
    let myData = cols.map((e) => {
      let val = values(pick(data[e], keys))
      const myArray = []
      for (let i = 0; i < val.length; i++) {
        let obj = {}
        if (typeof val[i] === 'string') {
          if (val[i].length) {
            obj.value = val[i]
            if (cellStyle === 'makeLink') {
              if (hasLinkIndex !== undefined && i === hasLinkIndex) {
                obj.makeLink = true
                obj.clickable = true
              } else if (hasLinkIndex !== undefined) {

              } else {
                obj.makeLink = true
              }
            }
            myArray.push(obj)
          }
        } else {
          obj.value = val[i]
          if (cellStyle === 'makeLink') {
            if (hasLinkIndex !== undefined && i === hasLinkIndex) {
              obj.makeLink = true
              obj.clickable = true
            } else if (hasLinkIndex !== undefined) {

            } else {
              obj.makeLink = true
            }
          }
          myArray.push(obj)
        }
      }
      if (myArray.length) {
        return myArray
      }
    })
    if (myData && myData[0]) {
      return myData
    } else {
      return {}
    }
  }

  getCellDataAsObject (keys, data, cellStyle, screenName, hasLinkIndex, filteredHeadersKeys) {
    var cols = keysIn(data)
    let val
    let propStore = {}
    let indexOfferId
    if (screenName === 'selected_offer_list') {
      keys = keys.filter(e => ['my_offer', 'lock'].indexOf(e) === -1)
    }

    let myData = cols.map((e, itemPosition) => {
      if (screenName === 'offer_details') {
        indexOfferId = indexOf(keys, 'offer_id')
      } else if (screenName === 'set_zone') {
        indexOfferId = indexOf(keys, 'zoneItemId')
      } else if (screenName === 'selected_offer_list') {
        indexOfferId = indexOf(keys, 'offer_id')
      }

      propStore = {
        lock: data[e].lock,
        my_offer: data[e].my_offer
      }

      val = values(pick(data[e], keys))
      const myArray = []
      for (let i = 0; i < val.length; i++) {
        let obj = {}
        let tempObject = val[i]
        if (screenName === 'landing') {
          if (typeof val[i] === 'object') {
            let myVal = tempObject.offerCount + '(' + tempObject.itemCount + ')'
            obj.value = myVal
            obj.object = tempObject.itemToOfferMapping
            if (tempObject.status) {
              obj.status = tempObject.status
            }
          } else {
            obj.value = val[i]
          }
          if (cellStyle === 'makeLink') {
            if (hasLinkIndex !== -1 && i === hasLinkIndex) {
              obj.makeLink = true
              obj.clickable = true
            } else if (hasLinkIndex !== -1) {

            } else {
              if (tempObject.offerCount !== 0) {
                obj.makeLink = true
              }
            }
          }
        } else if (screenName === 'offer_details' || screenName === 'selected_offer_list') {
          if (screenName === 'selected_offer_list') {
          }
          if (typeof val[i] === 'object') {
          } else {
            const offerId = val[indexOfferId]
            obj.id = offerId
            obj.value = val[i]
          }
          if (cellStyle === 'makeLink') {
            if (hasLinkIndex !== -1 && i === hasLinkIndex) {
              obj.makeLink = true
              obj.clickable = true
            } else if (hasLinkIndex !== -1) {

            } else {
              obj.makeLink = true
            }
          }
        } else if (screenName === 'approve_with_changes') {
          obj.value = val[i]
          if (cellStyle === 'makeEditable') {
            if (filteredHeadersKeys.indexOf(i) > -1) {
              obj.editable = true
              obj.index = i
              obj['itemIndex'] = itemPosition
            }
          }
        } else if (screenName === 'set_zone') {
          if (typeof val[i] === 'object') {
          } else {
            obj.id = val[indexOfferId]
            obj.index = i
            obj.value = val[i]
          }
          if (cellStyle === 'makeEditable') {
            if (filteredHeadersKeys.indexOf(i) > -1) {
              obj.editable = true
            }
          }
        }
        myArray.push(obj)
      }
      if (screenName === 'selected_offer_list') {
        myArray.splice(indexOfferId, 1)
      } else if (screenName === 'offer_details') {
        myArray.splice(indexOfferId, 1)
        myArray[this.deleted.key] = this.deleted.value
      } else if (screenName === 'landing') {
        myArray.unshift({value: e.toUpperCase()})
      }
      for (let prop in propStore) {
        if (!isNaN(Number(prop))) throw Error('Got property as a number')
        myArray[prop] = propStore[prop]
      }
      return myArray
    })
    if (screenName === 'approve_with_changes') {
      addAdditonalColumn(myData, val, cellStyle, filteredHeadersKeys)
    }
    return myData
  }
}

function addAdditonalColumn (myData, val, cellStyle, filteredHeadersKeys) {
  const myArray = []
  for (let i = 0; i < val.length; i++) {
    let obj = {}
    obj.index = i
    if (i === 0) {
      obj.value = 'Common Override'
    }
    if (cellStyle === 'makeEditable') {
      if (filteredHeadersKeys.indexOf(i) > -1) {
        obj.editable = true
        obj.commonOverride = true
        obj.itemIndex = myData.length
        obj.value = ''
      } else {
        obj.value = (obj.value === '' || typeof obj.value === 'undefined') ? '\xa0' : obj.value
      }
    }
    myArray.push(obj)
  }
  myData.push(myArray)
  return myData
}



// WEBPACK FOOTER //
// ./src/components/shared/flexTables/index.js